/**
 * Offline-aware Menu Items Hook
 * Uses SQLite when offline in Electron, falls back to Supabase when online
 * 
 * KEY FIX: Maintains a cache of online data to prevent empty state during offline transition
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useNetwork } from '@/contexts/NetworkContext';
import { useElectron } from '@/hooks/useElectron';
import { useMenuItems } from '@/hooks/useMenuItems';
import { MenuItem } from '@/types/index';
import { offlineLog, logCacheRead, logOfflineMode } from '@/utils/offlineDebug';

const COMPONENT = 'OfflineMenuItems';

interface OfflineMenuItemsResult {
  menuItems: MenuItem[];
  loading: boolean;
  error: string | null;
  isOffline: boolean;
  isFromLocalDb: boolean;
  refetch: () => Promise<void>;
}

export function useOfflineMenuItems(
  categoryId?: string,
  branchId?: string
): OfflineMenuItemsResult {
  const { isOnline, isElectronApp } = useNetwork();
  const { database } = useElectron();
  
  // Determine if we should use offline mode - BEFORE calling online hook
  const shouldUseOffline = isElectronApp && !isOnline && !!database;
  
  // State for offline data
  const [offlineMenuItems, setOfflineMenuItems] = useState<MenuItem[]>([]);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [offlineError, setOfflineError] = useState<string | null>(null);
  
  // CRITICAL: Track if offline data is ready (has been fetched at least once for current category)
  const [offlineDataReady, setOfflineDataReady] = useState(false);
  
  // CRITICAL: Cache online data so we can show it during offline transition
  // Use a map keyed by categoryId to handle category switches
  const cachedOnlineDataRef = useRef<Map<string, MenuItem[]>>(new Map());
  
  // Use the standard hook for online mode - DISABLED when offline to prevent network errors
  const onlineResult = useMenuItems(categoryId, branchId, { enabled: !shouldUseOffline });
  
  // Cache online data whenever it's available
  useEffect(() => {
    if (!shouldUseOffline && categoryId && onlineResult.menuItems && onlineResult.menuItems.length > 0) {
      cachedOnlineDataRef.current.set(categoryId, onlineResult.menuItems);
      offlineLog('info', `Cached ${onlineResult.menuItems.length} menu items for category ${categoryId}`, { component: COMPONENT, categoryId, branchId });
    }
  }, [shouldUseOffline, categoryId, onlineResult.menuItems, branchId]);
  
  // Debug logging for mode detection
  useEffect(() => {
    if (categoryId) {
      logOfflineMode(COMPONENT, shouldUseOffline, {
        isOnline,
        isElectronApp,
        hasDatabase: !!database,
        categoryId,
        branchId
      });
    }
  }, [shouldUseOffline, isOnline, isElectronApp, database, categoryId, branchId]);
  
  // Fetch from local SQLite database
  const fetchOfflineMenuItems = useCallback(async () => {
    if (!categoryId || !database) {
      setOfflineMenuItems([]);
      return;
    }
    
    setOfflineLoading(true);
    setOfflineError(null);
    
    try {
      offlineLog('info', `Fetching menu items from SQLite for category: ${categoryId}`, { component: COMPONENT, categoryId });
      const data = await database.getMenuItems(categoryId);
      
      // Transform SQLite data to match MenuItem interface
      const menuItems: MenuItem[] = (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        category_id: row.category_id as string,
        name: row.name as string,
        name_ar: row.name_ar as string | null,
        description: row.description as string | null,
        description_ar: row.description_ar as string | null,
        base_price: row.price as number,
        cost: row.cost as number | null,
        image_url: row.image_url as string | null,
        is_available: row.is_available === 1,
        is_active: row.is_active === 1,
        is_featured: row.is_featured === 1,
        sort_order: row.display_order as number,
        preparation_time: row.preparation_time as number | null,
        calories: row.calories as number | null,
        allergens: row.allergens ? JSON.parse(row.allergens as string) : null,
        tags: row.tags ? JSON.parse(row.tags as string) : null,
        assigned_printer_name: row.assigned_printer_name as string | null,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
      }));
      
      logCacheRead(COMPONENT, 'menuItems', menuItems.length, 'sqlite', branchId);
      
      if (menuItems.length === 0) {
        offlineLog('warn', 'SQLite returned 0 menu items - category may not be prefetched', { component: COMPONENT, categoryId });
      }
      
      setOfflineMenuItems(menuItems);
      setOfflineDataReady(true); // Mark offline data as ready
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load offline menu items';
      setOfflineError(errorMessage);
      offlineLog('error', `Error fetching from SQLite: ${errorMessage}`, { component: COMPONENT, categoryId }, err);
    } finally {
      setOfflineLoading(false);
    }
  }, [categoryId, branchId, database]);
  
  // Fetch offline data when needed OR when category changes while offline
  useEffect(() => {
    if (shouldUseOffline && categoryId) {
      setOfflineDataReady(false); // Reset ready state for new category
      fetchOfflineMenuItems();
    } else if (!shouldUseOffline) {
      // Reset offline data ready state when going online
      setOfflineDataReady(false);
    }
  }, [shouldUseOffline, categoryId, fetchOfflineMenuItems]);
  
  // Return appropriate result based on mode
  if (shouldUseOffline) {
    // Get cached online data for current category
    const cachedData = categoryId ? cachedOnlineDataRef.current.get(categoryId) || [] : [];
    
    // CRITICAL: If offline data isn't ready yet, use cached online data to prevent empty state
    const itemsToReturn = offlineDataReady ? offlineMenuItems : 
      (offlineMenuItems.length > 0 ? offlineMenuItems : cachedData);
    
    // Still loading if we're trying to fetch offline data and have neither offline nor cached data
    const isStillLoading = offlineLoading || (!offlineDataReady && offlineMenuItems.length === 0 && cachedData.length === 0);
    
    offlineLog('info', `Returning offline mode: ${itemsToReturn.length} items for category ${categoryId}, ready=${offlineDataReady}, loading=${isStillLoading}`, { component: COMPONENT, categoryId, branchId });
    
    return {
      menuItems: itemsToReturn,
      loading: isStillLoading,
      error: offlineError,
      isOffline: true,
      isFromLocalDb: offlineDataReady,
      refetch: fetchOfflineMenuItems,
    };
  }
  
  return {
    menuItems: onlineResult.menuItems || [],
    loading: onlineResult.loading,
    error: onlineResult.error ? String(onlineResult.error) : null,
    isOffline: false,
    isFromLocalDb: false,
    refetch: async () => { onlineResult.refetch(); },
  };
}

/**
 * Offline-aware hook for all menu items in a branch
 */
export function useOfflineAllMenuItems(branchId?: string): OfflineMenuItemsResult {
  const { isOnline, isElectronApp } = useNetwork();
  const { database } = useElectron();
  
  // State for offline data
  const [offlineMenuItems, setOfflineMenuItems] = useState<MenuItem[]>([]);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [offlineError, setOfflineError] = useState<string | null>(null);
  
  // Determine if we should use offline mode
  const shouldUseOffline = isElectronApp && !isOnline && database;
  
  // Fetch all menu items from local SQLite database
  const fetchOfflineAllMenuItems = useCallback(async () => {
    if (!branchId || !database) {
      setOfflineMenuItems([]);
      return;
    }
    
    setOfflineLoading(true);
    setOfflineError(null);
    
    try {
      const data = await database.getAllMenuItems(branchId);
      
      // Transform SQLite data to match MenuItem interface
      const menuItems: MenuItem[] = (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        category_id: row.category_id as string,
        name: row.name as string,
        name_ar: row.name_ar as string | null,
        description: row.description as string | null,
        description_ar: row.description_ar as string | null,
        base_price: row.price as number,
        cost: row.cost as number | null,
        image_url: row.image_url as string | null,
        is_available: row.is_available === 1,
        is_active: row.is_active === 1,
        is_featured: row.is_featured === 1,
        sort_order: row.display_order as number,
        preparation_time: row.preparation_time as number | null,
        calories: row.calories as number | null,
        allergens: row.allergens ? JSON.parse(row.allergens as string) : null,
        tags: row.tags ? JSON.parse(row.tags as string) : null,
        assigned_printer_name: row.assigned_printer_name as string | null,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
      }));
      
      setOfflineMenuItems(menuItems);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load offline menu items';
      setOfflineError(errorMessage);
      console.error('[OfflineAllMenuItems] Error fetching from SQLite:', err);
    } finally {
      setOfflineLoading(false);
    }
  }, [branchId, database]);
  
  // Fetch offline data when needed
  useEffect(() => {
    if (shouldUseOffline) {
      fetchOfflineAllMenuItems();
    }
  }, [shouldUseOffline, fetchOfflineAllMenuItems]);
  
  // For online mode, we don't have a direct "all menu items" hook
  // So we return empty and let components use category-based loading
  if (shouldUseOffline) {
    return {
      menuItems: offlineMenuItems,
      loading: offlineLoading,
      error: offlineError,
      isOffline: true,
      isFromLocalDb: true,
      refetch: fetchOfflineAllMenuItems,
    };
  }
  
  return {
    menuItems: [],
    loading: false,
    error: null,
    isOffline: false,
    isFromLocalDb: false,
    refetch: async () => {},
  };
}

export default useOfflineMenuItems;
